/**
 * The "use this template" handoff from a public share surface to the app
 * origin's signup: the share shell builds the params, the app origin reads them
 * after auth and resumes the copy.
 *
 * The `template_*` param names, their emission order, and the 60-minute
 * freshness window are a frozen cross-repo contract — the app-origin intake
 * parses exactly these. They are unsigned by design: they grant nothing, and
 * authorization for the copy stays server-side ("publication is the grant").
 *
 * Attribution params ride strictly after the frozen set — additive, never
 * interleaved. See ./signupUtm.
 */
import type { OrganizationUtm } from "@cohesive/api-client";

import { appendSignupUtmParams } from "./signupUtm";

export type TemplateSignupIntentKind = "canvas" | "file";
export type TemplateSignupIntentCta = "pill" | "header";

export type TemplateSignupIntent = {
  id: string;
  kind: TemplateSignupIntentKind;
  title?: string;
  author?: string;
  cta: TemplateSignupIntentCta;
  /** Epoch ms; stamped by buildTemplateSignupParams when absent. */
  ts?: number;
};

/** Intents older than this are ignored — an abandoned signup shouldn't resume days later. */
export const TEMPLATE_SIGNUP_INTENT_TTL_MS = 60 * 60 * 1000;

const STORAGE_KEY = "cohesive:template-signup-intent";

const KINDS: readonly string[] = ["canvas", "file"];
const CTAS: readonly string[] = ["pill", "header"];

export function buildTemplateSignupParams(
  intent: TemplateSignupIntent,
): URLSearchParams | null {
  const id = intent.id.trim();
  if (!id) return null;

  const params = new URLSearchParams();
  params.append("template_id", id);
  params.append("template_kind", intent.kind);

  const title = intent.title?.trim();
  if (title) params.append("template_title", title);

  const author = intent.author?.trim();
  if (author) params.append("template_author", author);

  params.append("template_cta", intent.cta);
  params.append("template_ts", String(intent.ts ?? Date.now()));

  return params;
}

/** The only place the `/signup` path literal lives. */
export function buildTemplateSignupPath(
  intent?: TemplateSignupIntent | null,
  utm?: OrganizationUtm | null,
): string {
  const params =
    (intent ? buildTemplateSignupParams(intent) : null) ??
    new URLSearchParams();
  appendSignupUtmParams(params, utm);
  const search = params.toString();
  return search ? `/signup?${search}` : "/signup";
}

export function parseTemplateSignupIntent(
  search: string | URLSearchParams,
  now: number = Date.now(),
): TemplateSignupIntent | null {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;

  const id = params.get("template_id")?.trim();
  if (!id) return null;

  const kind = params.get("template_kind");
  if (!kind || !KINDS.includes(kind)) return null;

  const cta = params.get("template_cta");
  if (!cta || !CTAS.includes(cta)) return null;

  const ts = Number(params.get("template_ts"));
  if (!Number.isFinite(ts) || now - ts > TEMPLATE_SIGNUP_INTENT_TTL_MS) {
    return null;
  }

  const title = params.get("template_title")?.trim();
  const author = params.get("template_author")?.trim();

  return {
    id,
    kind: kind as TemplateSignupIntentKind,
    ...(title ? { title } : {}),
    ...(author ? { author } : {}),
    cta: cta as TemplateSignupIntentCta,
    ts,
  };
}

export function stashTemplateSignupIntent(intent: TemplateSignupIntent): void {
  if (!intent.id.trim()) return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...intent, ts: intent.ts ?? Date.now() }),
    );
  } catch {
    // Storage unavailable (private mode / quota) — the redirect still carries
    // the intent in its query params.
  }
}

/** Consume-on-read: returns the stashed intent at most once, and only while fresh. */
export function consumeTemplateSignupIntent(
  now: number = Date.now(),
): TemplateSignupIntent | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(STORAGE_KEY);

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { id, kind, title, author, cta, ts } =
      parsed as Partial<TemplateSignupIntent>;
    if (typeof id !== "string" || !id.trim()) return null;
    if (typeof kind !== "string" || !KINDS.includes(kind)) return null;
    if (typeof cta !== "string" || !CTAS.includes(cta)) return null;
    if (typeof ts !== "number" || now - ts > TEMPLATE_SIGNUP_INTENT_TTL_MS) {
      return null;
    }

    return {
      id,
      kind,
      ...(title ? { title } : {}),
      ...(author ? { author } : {}),
      cta,
      ts,
    };
  } catch {
    return null;
  }
}
